// Web Push en el cliente: registra el service worker, gestiona la suscripción
// y la sincroniza con el backend. Gated por VITE_VAPID_PUBLIC_KEY.

import { notificationsAPI } from './api';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function isPushConfigured(): boolean {
  return !!VAPID_PUBLIC_KEY;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/sw.js');
  if (existing) return existing;
  return navigator.serviceWorker.register('/sw.js');
}

export interface PushState {
  supported: boolean;
  configured: boolean;
  permission: NotificationPermission;
  subscribed: boolean;
}

export async function getPushState(): Promise<PushState> {
  const supported = isPushSupported();
  const configured = isPushConfigured();
  if (!supported) {
    return { supported, configured, permission: 'denied', subscribed: false };
  }
  let subscribed = false;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    if (reg) subscribed = !!(await reg.pushManager.getSubscription());
  } catch {
    /* noop */
  }
  return { supported, configured, permission: Notification.permission, subscribed };
}

/** Pide permiso, suscribe y registra la suscripción en el backend. */
export async function enableWebPush(): Promise<void> {
  if (!isPushSupported()) throw new Error('Tu navegador no soporta notificaciones push.');
  if (!VAPID_PUBLIC_KEY) throw new Error('Push no configurado (falta VITE_VAPID_PUBLIC_KEY).');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Permiso de notificaciones denegado.');

  const reg = await getRegistration();
  await navigator.serviceWorker.ready;

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
    });
  }

  const json = sub.toJSON();
  await notificationsAPI.subscribe({
    platform: 'web',
    endpoint: sub.endpoint,
    keys: { p256dh: json.keys!.p256dh, auth: json.keys!.auth },
  });
}

/** Cancela la suscripción local y la borra del backend. */
export async function disableWebPush(): Promise<void> {
  const reg = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => {});
  await notificationsAPI.unsubscribe(endpoint).catch(() => {});
}
